/**
 * rPI Porter — Thread detail + mutation hooks (E-102 P1-7b)
 *
 * Backing API: `apps/kgc-web/src/api/porter-threads.ts`
 *
 * Patterns:
 *  - `usePorterThread(id)` polls every 60 s + refetches on window focus so new
 *    inbound mail surfaces without manual refresh. The interval matches the
 *    OmniDesk Gmail pull-cron cadence (~1-2 min, D-1) — the old 5 s Chatwoot
 *    webhook-echo cadence was oversized for email transport.
 *  - `useReplyToThread(id)` invalidates cache on success — the newly inserted
 *    OUTBOUND message comes back as part of the next `GET /threads/:id`.
 *    Optimistic bubble UI is handled in the page layer (needs local state for
 *    the clientId / status pill) — the mutation itself just fires the POST.
 *  - `usePatchThread(id)` uses optimistic cache mutation so status/assignee
 *    changes feel instant. On error we roll back to the previous snapshot.
 */

import {
  getThread,
  patchThread,
  replyToThread,
  type PatchThreadBody,
  type ReplyBody,
  type ThreadDetail,
} from '@/api/porter-threads';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const THREAD_POLL_MS = 60_000;

function threadQueryKey(id: string): readonly unknown[] {
  return ['porter', 'thread', id] as const;
}

export function usePorterThread(id: string) {
  return useQuery({
    queryKey: threadQueryKey(id),
    queryFn: () => getThread(id),
    refetchInterval: THREAD_POLL_MS,
    refetchOnWindowFocus: true,
    enabled: !!id,
  });
}

export function useReplyToThread(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReplyBody) => replyToThread(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: threadQueryKey(id) });
    },
  });
}

interface PatchContext {
  prev: { data: ThreadDetail } | undefined;
}

export function usePatchThread(id: string) {
  const queryClient = useQueryClient();
  return useMutation<Awaited<ReturnType<typeof patchThread>>, Error, PatchThreadBody, PatchContext>(
    {
      mutationFn: (body: PatchThreadBody) => patchThread(id, body),
      onMutate: async (body): Promise<PatchContext> => {
        // Cancel in-flight polls so they don't overwrite our optimistic write
        await queryClient.cancelQueries({ queryKey: threadQueryKey(id) });
        const prev = queryClient.getQueryData<{ data: ThreadDetail }>(threadQueryKey(id));
        if (prev) {
          queryClient.setQueryData<{ data: ThreadDetail }>(threadQueryKey(id), {
            ...prev,
            data: { ...prev.data, ...body },
          });
        }
        return { prev };
      },
      onError: (_err, _body, ctx) => {
        if (ctx?.prev) {
          queryClient.setQueryData(threadQueryKey(id), ctx.prev);
        }
      },
      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: threadQueryKey(id) });
      },
    }
  );
}
